<template>
  <div class="h-full grid grid-rows-[auto_1fr] overflow-hidden">
    <ThreadsNav :show-center="false" show-back>
      <template #right>
        <div v-if="store.saveLoading" class="flex items-center gap-2">
          <VanLoading />
          <span>{{ $t("Message.saving") }}</span>
        </div>
      </template>
    </ThreadsNav>

    <div class="overflow-auto">
      <div v-if="loading" class="text-center mt-32">
        <VanLoading color="#ABABAB" />
      </div>
      <div v-else-if="store.nodes.length === 0">
        <VanEmpty :description="$t('Message.noDialoguesAvailable')" />
      </div>
      <div
        v-for="(nodeId, idx) in store.nodes"
        v-else
        class="flex mx-3 gap-2.5 relative mb-3"
      >
        <div class="absolute left-0 right-0 top-5 h-1 bg-my-gray line z-0" />
        <template v-if="getNodeById(nodeId).type !== 1">
          <div class="w-[100px] flex flex-col justify-center items-center pt-8">
            <div
              v-for="parentNodeId in getNodeById(nodeId).parentNodeIdzz"
              class="w-[90px] py-1 text-center bg-neutral rounded mb-2.5 cursor-pointer"
            >
              {{ getNodeById(parentNodeId).name }}
            </div>
            <div class="flex gap-2">
              <div
                v-if="getNodeById(nodeId).parentNodeIdzz!.length<store.nodes.length-1"
                class="w-[40px] py-0.5 text-center bg-neutral rounded cursor-pointer"
                @click="clickShowNodes(nodeId, true)"
              >
                +
              </div>
              <div
                v-if="getNodeById(nodeId).parentNodeIdzz!.length > 0"
                class="w-[40px] py-0.5 text-center bg-neutral rounded cursor-pointer"
                @click="clickShowRemoveNodes(nodeId, true)"
              >
                -
              </div>
            </div>
          </div>
        </template>
        <div class="flex-1 bg-neutral rounded-xl p-2.5 relative z-1">
          <h2 class="font-bold mb-2.5">
            {{ getNodeById(nodeId).name }}
          </h2>
          <p class="bg-primary p-2 rounded">
            {{ getNodeById(nodeId).description }}
          </p>
          <div v-if="getNodeById(nodeId).type !== 1">
            <h3 class="my-1">{{ $t("game.activationCondition") }}</h3>
            <div class="flex gap-2 flex-wrap">
              <div
                v-if="getNodeById(nodeId).score !== null"
                class="py-1 px-4 bg-primary rounded-lg whitespace-nowrap cursor-pointer"
              >
                {{ store.getScoreTextByEx(getNodeById(nodeId).score!) }}
              </div>
              <div
                v-if="getNodeById(nodeId).score !== null"
                class="py-1 px-4 bg-primary rounded-lg cursor-pointer"
                @click="clickRemoveNodeScore(nodeId)"
              >
                -
              </div>
              <div
                v-else
                class="py-1 px-4 bg-primary rounded-lg cursor-pointer"
                @click="clickEditScore(nodeId)"
              >
                +
              </div>
            </div>
          </div>
        </div>
        <div
          v-if="getNodeById(nodeId).type !== 3"
          class="w-[100px] flex flex-col justify-center items-center pt-8"
        >
          <div
            v-for="childNodeId in getNodeById(nodeId).childNodeIdzz"
            class="w-[90px] py-1 text-center bg-neutral rounded overflow-hidden whitespace-nowrap text-ellipsis mb-2.5 cursor-pointer"
          >
            {{ getNodeById(childNodeId).name }}
          </div>

          <div class="flex gap-2">
            <div
              v-if="getNodeById(nodeId).childNodeIdzz!.length<store.nodes.length-1"
              class="w-[40px] py-0.5 text-center bg-neutral rounded cursor-pointer"
              @click="clickShowNodes(nodeId, false)"
            >
              +
            </div>
            <div
              v-if="getNodeById(nodeId).childNodeIdzz!.length > 0"
              class="w-[40px] py-0.5 text-center bg-neutral rounded cursor-pointer"
              @click="clickShowRemoveNodes(nodeId, false)"
            >
              -
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <ModalPublishGalGame />
  <ModalConditionSetting />

  <VanPopup v-model:show="showPicker" position="bottom" round>
    <VanPicker
      v-model="activeNode"
      :cancel-button-text="$t('Buttons.cancel')"
      :columns="columns"
      :confirm-button-text="$t('Buttons.momentSave')"
      :title="$t('game.selectContentNode')"
      @cancel="onCancel"
      @confirm="onConfirm"
    />
  </VanPopup>
</template>
<script lang="ts" setup>
import { useGalGameEditor } from "@/stores/galGameEditor";
import {
  PickerConfirmEventParams,
  showConfirmDialog,
  showFailToast,
} from "vant";
import { onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import i18n from "@/locales";

const store = useGalGameEditor();
const route = useRoute();

const loading = ref(true);
const showPicker = ref(false);
const columns = ref<any[]>([]);
const activeNode = ref<string[]>([]);
let isRemove = false;

const getNodeById = (id: number) => {
  return store.nodeMap[id];
};

const clickEditScore = (nodeId: number) => {
  const node = getNodeById(nodeId);
  store.activeNode = node;
  store.showConditionModal = true;
};

const clickRemoveNodeScore = async (nodeId: number) => {
  showConfirmDialog({
    title: i18n.global.t("ConfirmModal.promptTitle"),
    message: i18n.global.t("ConfirmModal.deleteActivationCondition"),
    confirmButtonText: i18n.global.t("Buttons.confirm"),
    cancelButtonText: i18n.global.t("Buttons.cancel"),
  }).then(async () => {
    const node = getNodeById(nodeId);
    node.score = null;
    store.saveNode();
  });
};

const onConfirm = async ({ selectedValues }: PickerConfirmEventParams) => {
  const { isParent } = store;
  const nodeId = Number((selectedValues[0] as string).split("-")[1]);
  const node = getNodeById(nodeId);
  if (!isRemove) {
    if (isParent) {
      store.activeNode.parentNodeIdzz!.push(nodeId);
      node.childNodeIdzz!.push(store.activeNode.id!);
    } else {
      store.activeNode.childNodeIdzz!.push(nodeId);
      node.parentNodeIdzz!.push(store.activeNode.id!);
    }
  } else {
    if (isParent) {
      store.activeNode.parentNodeIdzz = store.activeNode.parentNodeIdzz!.filter(
        (v) => v !== nodeId
      );
      node.childNodeIdzz = node.childNodeIdzz!.filter(
        (v) => v !== store.activeNode.id
      );
    } else {
      store.activeNode.childNodeIdzz = store.activeNode.childNodeIdzz!.filter(
        (v) => v !== nodeId
      );
      node.parentNodeIdzz = node.parentNodeIdzz!.filter(
        (v) => v !== store.activeNode.id
      );
    }
  }
  store.saveNode();
  showPicker.value = false;
};
const onCancel = () => {
  showPicker.value = false;
};

const clickShowNodes = (nodeId: number, isParent: boolean) => {
  const { beginningNode, plotList, endingList } = store;
  const node = getNodeById(nodeId);
  store.activeNode = node;
  store.isParent = isParent;
  let nodes: number[] = [];
  let filterMap: { [key: number]: boolean } = { [nodeId]: true };

  if (isParent) {
    node.parentNodeIdzz?.forEach((v) => (filterMap[v] = true));
  } else {
    node.childNodeIdzz?.forEach((v) => (filterMap[v] = true));
  }

  if (node.type === 1) {
    // 开头
    nodes = [...plotList, ...endingList];
  } else if (node.type === 2) {
    // 剧情
    nodes = isParent
      ? [beginningNode, ...plotList]
      : [...plotList, ...endingList];
  } else if (node.type === 3) {
    // 结局
    nodes = [beginningNode, ...plotList];
  }

  nodes = nodes.filter((id) => !filterMap[id]);

  if (nodes.length === 0)
    return showFailToast({
      message: i18n.global.t("Message.noSelectableNodeAvailable"),
    });

  columns.value = nodes.map((nodeId) => ({
    text: getNodeById(nodeId).name,
    value: "node-" + nodeId,
  }));
  isRemove = false;
  showPicker.value = true;
};

const clickShowRemoveNodes = (nodeId: number, isParent: boolean) => {
  let nodes: number[] = [];
  const node = getNodeById(nodeId);
  store.activeNode = node;
  store.isParent = isParent;
  if (isParent) {
    nodes = node.parentNodeIdzz!;
  } else {
    nodes = node.childNodeIdzz!;
  }
  columns.value = nodes.map((nodeId) => ({
    text: getNodeById(nodeId).name,
    value: "node-" + nodeId,
  }));
  isRemove = true;
  showPicker.value = true;
};

onMounted(async () => {
  const uuid = route.params.uuid as string;
  const { nodeId } = route.query;
  loading.value = true;
  if (store.activeNode.id !== Number(nodeId)) store.dialogList = [];
  const _nodeId = Number(nodeId);
  await store.initGalGame(uuid);
  await store.initNode(store.galGameId);
  store.activeNode = store.nodeMap[_nodeId];
  loading.value = false;
});
</script>

<style scoped>
.line::after,
.line::before {
  content: "";
  display: block;
  position: absolute;
  right: -1px;
  top: -3px;
  width: 14px;
  height: 3px;
  background: #ababab;
  rotate: 30deg;
}

.line:after {
  top: initial;
  bottom: -3px;
  rotate: -30deg;
}
</style>
